import {
  DropdownSymptom,
  NumericSymptom,
  SymptomModes,
  SymptomTypes
} from '../../../symptoms/symptom.model';

export enum NutritionTestKey {
  OMEGA_RATIO = 'OMEGA_RATIO',
  GUT_HEALTH = 'GUT_HEALTH',
  VITAMIN_D = 'VITAMIN_D',
  ANTIOXIDANT_PRYSM_IO = 'ANTIOXIDANT_PRYSM_IO'
}

// Render order for the section and for the PDF table.
export const NUTRITION_TEST_KEYS: string[] = [
  NutritionTestKey.OMEGA_RATIO,
  NutritionTestKey.GUT_HEALTH,
  NutritionTestKey.VITAMIN_D,
  NutritionTestKey.ANTIOXIDANT_PRYSM_IO
];

// Assessment methods this section applies to.
export const NUTRITION_TEST_MODES: SymptomModes[] = [SymptomModes.Advanced, SymptomModes.Silver];

export const NUTRITION_TEST_SCHEMA: {
  [key: string]: DropdownSymptom | NumericSymptom;
} = {
  // Omega-6:3 ratio from the Zinzino BalanceTest. Zinzino defines this marker as AA / EPA
  // (report p.11), not a whole-blood n-6:n-3 ratio — which is why the axis runs to 125:1
  // rather than the ~20:1 a whole-blood ratio would reach. Their bar grades three tiers:
  // Balanced below 3:1, Semi-Balanced 3:1-9:1, Unbalanced above 9:1. The extra colour
  // splits at 6/20/50 only smooth the arrow's travel; every label change sits on a Zinzino
  // boundary. The ratio is always normalized to X:1, so only the left-hand number is
  // entered — ':1' is the unit suffix. Lower is better, so bands run low-to-high (better
  // end first).
  [NutritionTestKey.OMEGA_RATIO]: {
    name: 'Omega Ratio',
    type: SymptomTypes.Numeric,
    modes: NUTRITION_TEST_MODES,
    infoPopoverContent:
      'Enter the Omega-6:3 ratio from the Zinzino BalanceTest as a single number — ' +
      'e.g. enter 38 for a 38:1 result. Zinzino grades below 3:1 as Balanced, ' +
      '3:1–9:1 as Semi-Balanced, and above 9:1 as Unbalanced.',
    units: ':1',
    min: 0,
    max: 125,
    step: 0.1,
    thermalScale: {
      green: '0-3',
      yellow: '3-6',
      'yellow orange': '6-9',
      orange: '9-20',
      'orange red': '20-50',
      red: '50-125',
      labels: {
        green: 'Balanced',
        yellow: 'Semi-Balanced',
        'yellow orange': 'Semi-Balanced',
        orange: 'Unbalanced',
        'orange red': 'Unbalanced',
        red: 'Unbalanced'
      }
    }
  },
  // Gut Health Index from the Zinzino Gut Health Test — the headline 0-100% score that
  // combines IPA level with the KYN:TRP, IPA:TRP and IPA:KYN ratios (report p.4). Higher is
  // better, so bands run high-to-low (better end first). Zinzino's bar breaks at 40/70/85;
  // its prose names only High/Moderate/Low, so 'Good' and 'Optimal' are our names for its
  // two green segments, keeping the wording this field's original dropdown used. The extra
  // splits at 55/20 are interpolation only.
  [NutritionTestKey.GUT_HEALTH]: {
    name: 'Gut Health Test Results',
    type: SymptomTypes.Numeric,
    modes: NUTRITION_TEST_MODES,
    infoPopoverContent:
      'Enter the Gut Health Index percentage from the Zinzino Gut Health Test — ' +
      'e.g. enter 63 for 63%. Zinzino grades 0–40% Low, 40–70% Moderate, ' +
      '70–85% Good, and 85–100% Optimal.',
    units: '%',
    min: 0,
    max: 100,
    thermalScale: {
      green: '100-85',
      yellow: '85-70',
      'yellow orange': '70-55',
      orange: '55-40',
      'orange red': '40-20',
      red: '20-0',
      labels: {
        green: 'Optimal',
        yellow: 'Good',
        'yellow orange': 'Moderate',
        orange: 'Moderate',
        'orange red': 'Low',
        red: 'Low'
      }
    }
  },
  [NutritionTestKey.VITAMIN_D]: {
    name: 'Vitamin D Test Results',
    type: SymptomTypes.Numeric,
    modes: NUTRITION_TEST_MODES,
    units: 'nmol/L',
    min: 0,
    max: 250,
    thermalScale: {
      green: '>50',
      yellow: '50-37.5',
      'yellow orange': '37.5-25',
      orange: '25-16',
      'orange red': '16-8',
      red: '8-0',
      labels: {
        red: 'Deficient',
        'orange red': 'Deficient',
        orange: 'Deficient',
        'yellow orange': 'Insufficient',
        yellow: 'Insufficient',
        green: 'Sufficient'
      }
    }
  },
  [NutritionTestKey.ANTIOXIDANT_PRYSM_IO]: {
    name: 'Antioxidant Prysm IO Score',
    type: SymptomTypes.Numeric,
    modes: NUTRITION_TEST_MODES,
    min: 0,
    max: 1000,
    thermalScale: {
      green: '1000-750',
      yellow: '750-550',
      'yellow orange': '550-400',
      orange: '400-300',
      'orange red': '300-200',
      red: '200-0',
      labels: {
        green: 'Preventing Active Damage',
        yellow: 'Optimum Protection Begins',
        'yellow orange': 'Keep Going',
        orange: 'Moderate',
        'orange red': 'Not Good',
        red: 'Likely Active Damage'
      }
    }
  }
};
